import { isValidISODateTime } from 'helpers/dateTime.helper/dateTime.helper'
import Datetime from 'utils/datetime/datetime'
import type { ElplanAlertMessage, ElplanErrorAlert, ElplanTranslate } from './elplan.alert.types'

// `alertStore` holds one alert at a time, so a multi-clause condition has to arrive as a single joined message.
export function formatElplanAlertMessage(alert: ElplanErrorAlert, translate: ElplanTranslate): string {
  return [alert, ...(alert.parts ?? [])].map((message) => translateAlertMessage(message, translate)).join(' ')
}

function translateAlertMessage(message: ElplanAlertMessage, translate: ElplanTranslate): string {
  if (!message.translatedValues) {
    return translate(message.key, message.values)
  }

  const translatedValues: [string, string][] = Object.entries(message.translatedValues).map(([name, key]) => [
    name,
    translate(key),
  ])
  return translate(message.key, { ...message.values, ...Object.fromEntries(translatedValues) })
}

/**
 * The backend localizes start times to the system's zone and keeps the offset, so rendering them
 * verbatim would show that offset as the viewer's; re-anchoring on the instant fixes that. The date
 * stays because a send can include a bid that starts on the previous day.
 */
export function formatBidStartTimes(startTimes: string[]): string {
  const chronologicalStartTimes = toChronologicalOrder(startTimes)
  const formatted = chronologicalStartTimes.map(formatBidStartTime)
  // On the autumn DST fall-back two distinct instants share one wall-clock time (02:00 at +02:00,
  // then 02:00 again at +01:00), so their rendered strings collide and the operator cannot tell the
  // repeated-hour bids apart. Append the offset only where one rendered time stands for two instants.
  const instantsByRenderedTime = new Map<string, Set<string>>()
  chronologicalStartTimes.forEach((startTime, index) => {
    const instant = toBidStartInstant(startTime)
    if (instant) {
      const instants = instantsByRenderedTime.get(formatted[index]) ?? new Set<string>()
      instantsByRenderedTime.set(formatted[index], instants.add(instant))
    }
  })
  return chronologicalStartTimes
    .map((startTime, index) => {
      const value = formatted[index]
      const isRepeatedHour = (instantsByRenderedTime.get(value)?.size ?? 0) > 1
      return isRepeatedHour ? `${value} (${Datetime.getLocalUtcOffset(startTime)})` : value
    })
    .join(', ')
}

/** Drops a start time naming an instant already listed, whatever offset it is written in. */
export function withoutRepeatedInstants(startTimes: string[]): string[] {
  const seen = new Set<string>()
  return startTimes.filter((startTime) => {
    const identity = toBidStartInstant(startTime) ?? startTime
    if (seen.has(identity)) {
      return false
    }
    seen.add(identity)
    return true
  })
}

function toChronologicalOrder(startTimes: string[]): string[] {
  const parsed = startTimes.flatMap((startTime) => {
    const instant = toBidStartInstant(startTime)
    return instant ? [{ startTime, instant }] : []
  })
  parsed.sort((a, b) => Datetime.compare(a.instant, b.instant))
  const unparsed = startTimes.filter((startTime) => toBidStartInstant(startTime) === undefined)
  return [...parsed.map(({ startTime }) => startTime), ...unparsed]
}

/**
 * A value that is not the ISO instant the backend sends is shown as it arrived: the permissive `Date`
 * parser coerces strings like `'2026'` to a fabricated date, and this runs inside the mutation's
 * `onError`, so a strict check keeps one malformed entry from costing the operator the whole alert.
 */
function toBidStartInstant(startTime: string): ISODateTime | undefined {
  return isValidISODateTime(startTime) ? new Datetime(startTime).ISOString : undefined
}

function formatBidStartTime(startTime: string): string {
  const instant = toBidStartInstant(startTime)
  return instant ? Datetime.toLocalTime(instant, 'longDay') : startTime
}
